/*
 * Copyright (c) 2023 ACG Business Analytics Inc. All rights reserved.
 *
 * This file is part of a proprietary software system of ACG Business Analytics Inc.
 * No part of this file may be copied, modified, sold, distributed, or used in any
 * way without the written permission of ACG Business Analytics Inc.
 *
 */

/* eslint-disable camelcase */
export interface BaeRoute {
    label: string;
    path: string;
    icon?: string;
}

export const RouteMap = {
    callback: {
        label: 'Callback',
        path: '/callback',
    },

    login: {
        label: 'Log in',
        path: '/login',
    },

    notAuthorized: {
        label: 'Not Authorized',
        path: '/not-authorized',
    },

    organizationSettings: {
        label: 'Organization Settings',
        path: '/organization',
    },

    pricingPresets: {
        label: 'Pricing Presets',
        path: '/organization/pricing-presets',
    },

    accountSettings: {
        label: 'Account Settings',
        path: '/account',
    },

    memberships: {
        label: 'Memberships',
        path: '/memberships',
    },

    scenarios: {
        label: 'Scenarios',
        path: '/scenarios',
    },

    overview: {
        label: 'Overview',
        path: '/overview',
    },

    libraryManagement: {
        label: 'Libraries',
        path: '/library-management',
    },

    labelsManagement: {
        label: 'Data Center Labels Management',
        path: 'labels',
    },

    templates: {
        label: 'Templates',
        path: '/templates',
    },

    templateCase: {
        label: 'TemplateCase',
        path: '/templates/:useCase',
    },

    scenario: {
        label: 'Scenario',
        path: '/scenarios/:modelId',
    },

    ...getScenarioDashboardRouteMap(),

    ...getMicrodataRouteMap(),

    libraryDesigner: {
        label: 'Library Designer',
        path: '/library-designer/:libraryId',
    },

    modelDesigner: {
        label: 'Model Designer',
        path: 'model-designer',
    },

    comparison: {
        label: 'Compare Scenarios',
        path: '/comparison',
    },

    shared: {
        label: 'Shared',
        path: '/shared/:sharedId',
    },

    // userManagement: {
    //     label: 'User Management',
    //     path: 'user-management',
    //     icon: '',
    // },

    // simpleTemplate: {
    //     label: 'Template',
    //     path: 'template',
    // },
} as const;

function getScenarioDashboardRouteMap() {
    return {
        settings: {
            label: 'Settings',
            path: 'settings',
        },

        kpis: {
            label: 'KPIs',
            path: 'kpis',
        },

        services: {
            label: 'Services',
            path: 'services',
        },

        expenses: {
            label: 'Expenses',
            path: 'expenses',
        },

        financials: {
            label: 'Financials',
            path: 'financials',
        },

        topology: {
            label: 'Topology',
            path: 'topology',
        },

        inventory: {
            label: 'Inventory',
            path: 'inventory',
        },

        connmap: {
            label: 'Connection Map',
            path: 'connmap',
        },
    } as const;
}

function getMicrodataRouteMap() {
    return {
        microdata: {
            label: 'Microdata',
            path: 'microdata/:endpoint',
        },
        microdata_regions: {
            label: 'Regions',
            path: 'microdata/regions',
        },
        microdata_segments: {
            label: 'Segments',
            path: 'microdata/segments',
        },
        microdata_markets: {
            label: 'Markets',
            path: 'microdata/markets',
        },
        microdata_tenants: {
            label: 'Tenants',
            path: 'microdata/tenants',
        },
        microdata_datacenters: {
            label: 'Datacenters',
            path: 'microdata/datacenters',
        },
        microdata_endpoints: {
            label: 'End Points',
            path: 'microdata/endpoints',
        },
        microdata_servicesUsage: {
            label: 'Services',
            path: 'microdata/svcusage',
        },
        microdata_serviceCashFlows: {
            label: 'Service Cash Flows',
            path: 'microdata/servicecf',
        },
        microdata_dcAppAvailability: {
            label: 'DC App Availability',
            path: 'microdata/appavail',
        },
        microdata_appConnections: {
            label: 'DC App Connections',
            path: 'microdata/appconnections',
        },
        microdata_dcAppDemand: {
            label: 'DC App Demand',
            path: 'microdata/appdemand',
        },
        microdata_dcAppLicenses: {
            label: 'DC App Licenses',
            path: 'microdata/applicenses',
        },
        microdata_dcAppCashFlows: {
            label: 'DC App Cash Flows',
            path: 'microdata/dcappcf',
        },
        microdata_cpeHardware: {
            label: 'CPE Hardware',
            path: 'microdata/cpehw',
        },
        microdata_cpeCashFlows: {
            label: 'CPE Cash Flows',
            path: 'microdata/cpecf',
        },
        microdata_cpeAppLicenses: {
            label: 'CPE Licenses',
            path: 'microdata/cpeapps',
        },
        microdata_cpeAppCashFlows: {
            label: 'CPE App Cash Flows',
            path: 'microdata/cpeappcf',
        },
        microdata_serversUsage: {
            label: 'Servers',
            path: 'microdata/serverusage',
        },
        microdata_serverCashFlows: {
            label: 'Server Cash Flows',
            path: 'microdata/servercf',
        },
        microdata_virtualDataCenters: {
            label: 'Virtual Data Centers',
            path: 'microdata/virtualdcs',
        },
        microdata_endPointTransport: {
            label: 'End Point Transport',
            path: 'microdata/endpointtransport',
        },
        microdata_endPointTransportCashFlows: {
            label: 'End Point Transport Cash Flows',
            path: 'microdata/edptransportcf',
        },
        microdata_fixedFteCashFlows: {
            label: 'Fixed FTE Cash Flows',
            path: 'microdata/fixedftecashflows',
        },
        microdata_dynamicFteCashFlows: {
            label: 'Dynamic FTE Cash Flows',
            path: 'microdata/dynamicftecashflows',
        },
    } as const;
}
